import type { CSSProperties, ReactNode } from "react";
import { cx } from "@/utils/cx";

const GLASS_SHADOW =
  "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.28),inset_0_-1px_0_0_rgb(255_255_255/0.06),0_10px_30px_-12px_rgb(0_0_0/0.55)]";

const RADIUS = {
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  full: "rounded-full",
} as const;

type GlassCardProps = {
  radius?: keyof typeof RADIUS;
  className?: string;
  style?: CSSProperties;
  /** Style for the inset top-highlight gradient overlay (e.g. its fade-in). */
  overlayStyle?: CSSProperties;
  children: ReactNode;
};

/**
 * A single glass card: translucent white fill, hairline white border, an inset
 * top highlight, backdrop blur and a deep soft shadow. Callers supply the blur
 * and entrance animation through `className` / `style` because these differ per
 * page (a rail card materialises with `glass-in`; a dealt card reveals its blur
 * through a transition).
 */
export function GlassCard({ radius = "xl", className, style, overlayStyle, children }: GlassCardProps) {
  return (
    <div
      className={cx(
        "relative overflow-hidden border border-white/20 bg-white/12",
        RADIUS[radius],
        GLASS_SHADOW,
        className,
      )}
      style={style}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-linear-to-b from-white/14 via-white/2 to-white/6"
        style={overlayStyle}
      />
      {children}
    </div>
  );
}
